import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useState, useEffect } from 'react'
import { Link, useRoute, useLocation } from 'wouter'
import { ArrowLeft, User, DollarSign, Calendar, CheckCircle, XCircle, Clock, Eye, MessageSquare, Send } from 'lucide-react'

import toast from 'react-hot-toast'
import { RfpResponse } from '@/types'
import { buildApiUrl } from '../config/api'

export function RfpResponses() {

  const [, setLocation] = useLocation()
  const [, params] = useRoute('/rfp/:rfpId/responses')
  const rfpId = params?.rfpId
  
  const [responses, setResponses] = useState<RfpResponse[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchResponses = async () => {
      if (!rfpId) {
        setError('RFP ID is required')
        setIsLoading(false)
        return
      }

      try {
        const token = localStorage.getItem('token')
        if (!token) {
          toast.error('Authentication required')
          setLocation('/login')
          return
        }

        const response = await fetch(buildApiUrl(`/api/responses/rfp/${rfpId}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })

        if (response.ok) {
          const data = await response.json()
          setResponses(data.data)
        } else if (response.status === 404) {
          setError('RFP not found or you do not have access')
        } else {
          setError('Failed to load responses')
        }
      } catch (error) {
        console.error('Error fetching responses:', error)
        setError('Failed to load responses')
      } finally {
        setIsLoading(false)
      }
    }

    fetchResponses()
  }, [rfpId, setLocation])

  const updateResponseStatus = async (responseId: string, status: string) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      const response = await fetch(buildApiUrl(`/api/responses/${responseId}/status`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      })

      if (response.ok) {
        if (status === 'ACCEPTED') {
          toast.success('Response accepted! Contact information is now shared between vendor and supplier.')
        } else {
          toast.success(`Response ${status.toLowerCase()} successfully`)
        }
        // Refresh responses
        const updatedResponses = responses.map(resp => 
          resp.id === responseId ? { ...resp, status: status as any } : resp
        )
        setResponses(updatedResponses)
      } else {
        const errorData = await response.json()
        toast.error(errorData.error || 'Failed to update response status')
      }
    } catch (error) {
      console.error('Error updating response status:', error)
      toast.error('Failed to update response status')
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="h-4 w-4 text-yellow-500" />
      case 'REVIEWED':
        return <Eye className="h-4 w-4 text-blue-500" />
      case 'ACCEPTED':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'REJECTED':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'WITHDRAWN':
        return <XCircle className="h-4 w-4 text-gray-500" />
      default:
        return <Clock className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'REVIEWED':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'ACCEPTED':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'REJECTED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'WITHDRAWN':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Link href="/request-for-proposal-marketplace">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Loading Responses...</h1>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading responses...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Link href="/request-for-proposal-marketplace">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Error</h1>
            <p className="text-muted-foreground">{error}</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">{error}</p>
            <Link href="/request-for-proposal-marketplace">
              <Button>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to RFPs
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link href={`/rfp/${rfpId}`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">RFP Responses</h1>
            <p className="text-muted-foreground">
              {responses.length} response{responses.length !== 1 ? 's' : ''} received
            </p>
          </div>
        </div>
      </div>

      {/* Responses List */}
      {responses.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No responses yet</h3>
            <p className="text-muted-foreground">
              Responses to your RFP will appear here once vendors submit their proposals.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {responses.map((response) => (
            <Card key={response.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{response.user.name}</CardTitle>
                      <CardDescription>{response.user.company}</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(response.status)}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(response.status)}`}>
                      {response.status.toLowerCase()}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Initial Message */}
                <div>
                  <h4 className="font-medium mb-2">Initial Message</h4>
                  <p className="text-muted-foreground">{response.message}</p>
                </div>

                {/* Key Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Budget:</span>
                    <span className="text-sm font-medium">${response.budget.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Timeline:</span>
                    <span className="text-sm font-medium">{response.timeline}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Submitted:</span>
                    <span className="text-sm font-medium">
                      {new Date(response.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Detailed Proposal */}
                <div>
                  <h4 className="font-medium mb-2">Detailed Proposal</h4>
                  <p className="text-muted-foreground whitespace-pre-wrap">{response.proposal}</p>
                </div>

                {/* Status Actions */}
                {response.status === 'PENDING' && (
                  <div className="flex space-x-2 pt-4 border-t">
                    <Button
                      size="sm"
                      onClick={() => updateResponseStatus(response.id, 'REVIEWED')}
                    >
                      Mark as Reviewed
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateResponseStatus(response.id, 'ACCEPTED')}
                    >
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateResponseStatus(response.id, 'REJECTED')}
                    >
                      Reject
                    </Button>
                  </div>
                )}

                {response.status === 'REVIEWED' && (
                  <div className="flex space-x-2 pt-4 border-t">
                    <Button
                      size="sm"
                      onClick={() => updateResponseStatus(response.id, 'ACCEPTED')}
                    >
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateResponseStatus(response.id, 'REJECTED')}
                    >
                      Reject
                    </Button>
                  </div>
                )}

                {response.status === 'ACCEPTED' && (
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                        ✓ Response accepted - Contact information and messaging now available
                      </span>
                    </div>
                    <Link href={`/rfp/${rfpId}`}>
                      <Button size="sm" variant="outline">
                        <Send className="mr-1 h-3 w-3" />
                        Message
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
} 